<script>
export default {
  props: {
    data1: Array,
    columns: Array,
    columnsDisplayed: Object,
    filterKey: String,
    perPage: Number
  },
  data() {
    return {
      sortKey: '',
      sortOrders: this.columns.reduce((o, key) => ((o[key] = 1), o), {}),
      page: 1,
      pages: [],
      data: []
    }
  },

  computed: {
    displayedData() {
      return this.paginate(this.filteredData(this.data1));
    },
    displayedDataAll() {
      return this.filteredData(this.data1);
    }
  },

  watch: {
    displayedData() {
      this.setPages();
    }
  },
  methods: {
    onImageLoadFailure(event) {
      event.target.src = 'https://cdn1.iconfinder.com/data/icons/unicons-line-vol-2/24/cloud-question-512.png'
    },
    sortBy(key) {
      this.sortKey = key
      this.sortOrders[key] = this.sortOrders[key] * -1
    },
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    // Logos SVG des plateformes (public/payment-icons), même jeu que la vue journalière
    gatewayIcon(gateway) {
      const known = ['paypal', 'stripe', 'dedipass', 'xmoney', 'nowpayments', 'coinbase', 'bitpay', 'qonto']
      const key = String(gateway || '').toLowerCase()
      return known.includes(key) ? `/payment-icons/${key}.svg` : null
    },
    setPages() {
      this.pages = [];
      let numberOfPages = Math.ceil(this.filteredData(this.data1).length / this.perPage);
      for (let index = 1; index <= numberOfPages; index++) {
        this.pages.push(index);
      }
    },
    filteredData() {
      const sortKey = this.sortKey
      const filterKey = this.filterKey && this.filterKey.toLowerCase()
      const order = this.sortOrders[sortKey] || 1
      let data = this.data1
      if (filterKey) {
        data = data.filter((row) => {
          return Object.keys(row).some((key) => {
            return String(row[key]).toLowerCase().indexOf(filterKey) > -1
          })
        })
      }
      if (sortKey) {
        data = data.slice().sort((a, b) => {
          a = a[sortKey]
          b = b[sortKey]
          return (a === b ? 0 : a > b ? 1 : -1) * order
        })
      }
      return data
    },
    paginate(data) {
      let page = this.page;
      let perPage = this.perPage;
      let from = (page * perPage) - perPage;
      let to = (page * perPage);
      return data.slice(from, to);
    }
  }
}
</script>

<script setup>
import {ChevronDownIcon, ChevronUpIcon} from '@heroicons/vue/solid'
import {ChevronLeftIcon, ChevronRightIcon} from '@heroicons/vue/solid'

</script>

<template>

  <div v-if="displayedData.length">
    <table
        class="min-w-full divide-y divide-gray-300"
    >
      <thead class="bg-gray-50">
      <tr>
        <th
            v-for="(item, index) in columnsDisplayed"
            :key="index"
            :class="{ active: sortKey == index }"
            class="px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-700"
            @click="sortBy(index)"
        >
          <a
              href="#"
              class="group inline-flex"
          >
            {{ capitalize(item) }}
            <span class="ml-1.5 flex-none rounded bg-gray-200 text-gray-900 group-hover:bg-gray-300">
              <ChevronDownIcon
                  v-if="sortOrders[index] > 0"
                  class="h-4 w-4"
                  aria-hidden="true"
              />
              <ChevronUpIcon
                  v-if="sortOrders[index] < 0"
                  class="h-4 w-4"
                  aria-hidden="true"
              />

            </span>
          </a>
          <span
              class="arrow"
              :class="sortOrders[index] > 0 ? 'asc' : 'dsc'"
          />
        </th>
      </tr>
      </thead>
      <tbody class="divide-y divide-gray-200 bg-white">
      <tr
          v-for="entry in displayedData"
          :key="entry"
      >
        <td
            v-for="(item, index) in columnsDisplayed"
            :key="index"
            class="overflow-hidden  whitespace-wrap px-2 py-1 text-sm text-gray-500"
        >
          <span v-if="index === 'type'"
                :class="[ entry['type'] ==='payment' || entry['type'] ==='paiement' ? 'inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800': 'inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800']">
             {{ entry['type'] }}
            </span>

          <div v-else-if="index === 'amount'" class="whitespace-nowrap">
            <span class="text-gray-900">{{ entry['amount'] }}</span>
            <span class="ml-1 text-xs text-gray-500" v-if="entry['local_amount'] !== entry['amount']">{{ entry['local_amount'] }}
              {{ entry['local_currency'] }}</span>
          </div>

          <div v-else-if="index === 'date'" class="min-w-[92px] whitespace-nowrap">
            {{ entry['date'] }}
          </div>

          <div v-else-if="index === 'gateway'" class="flex items-center gap-1.5 whitespace-nowrap">
            <img v-if="gatewayIcon(entry['gateway'])" :src="gatewayIcon(entry['gateway'])"
                 :alt="entry['gateway']" class="h-4 w-4 rounded-sm shrink-0"/>
            <span>{{ entry['gateway'] ? capitalize(String(entry['gateway'])) : '' }}</span>
          </div>

          <div v-else-if="index === 'description'" class="max-w-[220px] truncate" :title="entry['description']">
            {{ entry['description'] }}
          </div>

          <div v-else-if="index === 'whmcs_amount'">
          <span
              :class="[ entry['whmcs_amount'] === entry['amount'] ? 'inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800': 'inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800']">
             {{ entry[index] }}
            </span>
          </div>


          <div v-else>
            {{ entry[index] }}
          </div>

        </td>
      </tr>
      </tbody>
    </table>

    <div class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
      <div class="flex-1 flex justify-between sm:hidden">
        <a href="#" v-if="page != 1" @click="page--"
           class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
          Previous </a>
        <a href="#" @click="page++" v-if="page < pages.length"
           class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
          Next </a>
      </div>
      <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p class="text-sm text-gray-700">
            Showing
            {{ ' ' }}
            <span class="font-medium">{{ page }}</span>
            {{ ' ' }}
            to
            {{ ' ' }}
            <span class="font-medium">{{ pages.length }}</span>
            {{ ' ' }}
            of
            {{ ' ' }}
            <span class="font-medium">{{ displayedDataAll.length }}</span>
            {{ ' ' }}
            results
          </p>
        </div>
        <div>
          <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <a href="#" v-if="page != 1" @click="page--"
               class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
              <span class="sr-only">Previous</span>
              <ChevronLeftIcon class="h-5 w-5" aria-hidden="true"/>
            </a>
            <!-- Current: "z-10 bg-indigo-50 border-indigo-500 text-indigo-600", Default: "bg-white border-gray-300 text-gray-500 hover:bg-gray-50" -->
            <a href="#" aria-current="page" v-bind:key="pageNumber" type="button"
               v-for="pageNumber in pages.slice(page-1, page+5)" @click="page = pageNumber"
               :class="[ pageNumber === page ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600 relative inline-flex items-center px-4 py-2 border text-sm font-medium': 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 relative inline-flex items-center px-4 py-2 border text-sm font-medium']">
              {{ pageNumber }}</a>

            <a href="#" @click="page++" v-if="page < pages.length"
               class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
              <span class="sr-only">Next</span>
              <ChevronRightIcon class="h-5 w-5" aria-hidden="true"/>
            </a>
          </nav>
        </div>
      </div>
    </div>
  </div>
  <div
      v-else-if="displayedData.length === 0"
      class="bg-white"
  >
    <div class="max-w-7xl mx-auto text-center py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
      <h2 class="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
        <span class="block">Il n'y a aucune transaction ici !</span>
        <span class="block"></span>
      </h2>

    </div>
  </div>

</template>
